import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { Feedback, Turn } from "@app/shared";

type State = {
  /** DO セッションの ID。3軸フィードバックのサーバ生成の取得に使う。 */
  sessionId: string | null;
  turns: Turn[];
  hintsUsed: number;
};

type FetchState =
  | { phase: "loading" }
  | { phase: "ready"; feedback: Feedback }
  | { phase: "unavailable" };

const POLL_INTERVAL_MS = 2000;
// 生成は Gemini Flash（thinkingあり）で本番実測 約24秒。余裕をみて約48秒待つ。
// 超えたらローカル集計にフォールバックする。
const POLL_MAX_ATTEMPTS = 24;

/**
 * フィードバックの表示。生成は Gemini Flash（サーバ側）で行い、ここは取得して出すだけ。
 * 生成が間に合わない／失敗した場合は、文字起こしから数えられる範囲だけを出す。
 *
 * 表示の原則：
 *  - 数値スコアや星は使わない。LLM判定はブレるため、ブレた瞬間に信頼を失う
 *  - 「8回中1回」のように数えられる事実で示す
 *  - 「次に試すこと」は1件だけ。3件渡してもターゲット下限の層は消化できない
 */
export function FeedbackPage() {
  const { state } = useLocation() as { state: State | null };
  const [fetchState, setFetchState] = useState<FetchState>({ phase: "loading" });

  const sessionId = state?.sessionId ?? null;

  useEffect(() => {
    if (!sessionId) {
      setFetchState({ phase: "unavailable" });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const res = await fetch(`/api/sessions/${sessionId}/feedback`);
        if (cancelled) return;
        if (res.status === 200) {
          const data = (await res.json()) as { feedback: Feedback };
          setFetchState({ phase: "ready", feedback: data.feedback });
          return;
        }
        // 202 pending。上限まで再試行し、超えたらフォールバック表示。
        if (attempts >= POLL_MAX_ATTEMPTS) {
          setFetchState({ phase: "unavailable" });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setFetchState({ phase: "unavailable" });
      }
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (!state) {
    return (
      <div className="mx-auto max-w-md p-6">
        <p className="text-stone-600">セッションの情報がありません。</p>
        <Link to="/" className="mt-4 block text-sm underline">
          最初に戻る
        </Link>
      </div>
    );
  }

  const userTurns = state.turns.filter((t) => t.role === "user");
  // フォールバック用のローカル集計（サーバ生成が使えないとき）。
  const localQuestions = userTurns.filter((t) => t.text.includes("?")).length;

  return (
    <div className="mx-auto max-w-md p-6">
      <h1 className="text-xl font-semibold text-stone-800">おつかれさまでした</h1>

      {fetchState.phase === "loading" ? (
        <p className="mt-6 text-sm text-stone-500">ふりかえりを準備しています…</p>
      ) : fetchState.phase === "ready" ? (
        <>
          <section className="mt-6 space-y-3">
            <Row
              label="相槌を打てた回数"
              value={`${fetchState.feedback.totalTurns} 回中 ${fetchState.feedback.backchannelCount} 回`}
            />
            <Row
              label="質問で返せた回数"
              value={`${fetchState.feedback.totalTurns} 回中 ${fetchState.feedback.questionReturnCount} 回`}
            />
            <Row
              label="話題を広げられた回数"
              value={`${fetchState.feedback.totalTurns} 回中 ${fetchState.feedback.topicExpansionCount} 回`}
            />
            <Row label="ヒントを見た回数" value={`${fetchState.feedback.hintsUsed} 回`} />
          </section>

          <section className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-xs font-medium text-amber-800">次の1回で試すこと</p>
            <p className="mt-1 text-[15px] leading-relaxed text-stone-800">
              {fetchState.feedback.nextTry}
            </p>
          </section>
        </>
      ) : (
        // フォールバック：サーバ生成が間に合わなかった。数えられる事実だけを出す。
        <>
          <section className="mt-6 space-y-3">
            <Row label="話した回数" value={`${userTurns.length} 回`} />
            <Row
              label="質問で返せた回数"
              value={`${userTurns.length} 回中 ${localQuestions} 回`}
            />
            <Row label="ヒントを見た回数" value={`${state.hintsUsed} 回`} />
          </section>

          <section className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-xs font-medium text-amber-800">次の1回で試すこと</p>
            <p className="mt-1 text-[15px] leading-relaxed text-stone-800">
              相手の話に「それいいね」と一言反応してから、質問を返してみましょう。
            </p>
          </section>
        </>
      )}

      <section className="mt-6">
        <p className="text-xs text-stone-400">
          聞き取り結果（実際の発音と異なる場合があります）
        </p>
        <ul className="mt-2 space-y-2">
          {state.turns.map((t, i) => (
            <li
              key={i}
              className={
                t.role === "user"
                  ? "rounded-lg bg-blue-50 px-3 py-2 text-sm"
                  : "rounded-lg bg-stone-100 px-3 py-2 text-sm"
              }
            >
              <span className="text-xs text-stone-500">
                {t.role === "user" ? "あなた" : "相手"}
              </span>
              <p className="text-stone-800">{t.text}</p>
            </li>
          ))}
        </ul>
      </section>

      <Link
        to="/"
        className="mt-8 block rounded-xl bg-stone-800 py-4 text-center font-semibold text-white"
      >
        もう一度練習する
      </Link>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between border-b border-stone-100 pb-2">
      <span className="text-sm text-stone-500">{label}</span>
      <span className="text-[15px] font-medium tabular-nums text-stone-800">
        {value}
      </span>
    </div>
  );
}
